<template ref="searchContainer">
  <AppSkeletonSearch :query="query" :loading="entities.length === 0">
    <template v-slot:graph>
      <SearchGraph
        :entities="entities"
        :analysis="analysis"
        :clusters="clusters"
        v-model:activeCluster="activeCluster"
        :layout="layout"
        :backdropActive="backdropActive"
        :allEdges="allEdges"
        :viewEventEmitter="viewEventEmitter"
        :selected="selectedEntityIds"
        :filtered="filterEntityIds"
        @clickNode="clickNode"
        @mouseleaveNode="contextMenu = null"
        @filter="filterNodes"
        :disableBackdrop="analysisConfigurations[currentAnalysis].disableBackdrop"
        :disableEdges="analysisConfigurations[currentAnalysis].disableEdges"
      />
      <GraphControls
        @manipulateView="manipulateView"
        @changeAnalysis="switchAnalysis"
        @changeLayout="(newLayout) => (layout = newLayout)"
        :currentLayout="layout"
        :disableAnalysis="entities.length < 20"
        :currentAnalysis="currentAnalysis"
        :backdrop="{
          active: backdropActive,
          disabled: analysisConfigurations[currentAnalysis].disableBackdrop,
        }"
        @toggleBackdrop="backdropActive = !backdropActive"
        :allEdges="{
          active: allEdges,
          disabled: analysisConfigurations[currentAnalysis].disableEdges,
        }"
        @toggleAllEdges="allEdges = !allEdges"
        :entities="entities"
        :clusters="clusters"
        :analysis="analysis"
        @explore="startExplore"
      />
      <div
        v-if="contextMenu"
        class="
          absolute
          w-56
          py-2
          bg-gray-800
          shadow-md
          rounded-lg
          backdrop-filter backdrop-blur-lg
          bg-opacity-50
          text-left text-white
          divide-y divide-solid divide-black divide-opacity-25
        "
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="my-1">
          <h3 class="px-2 font-semibold text-sm py-1">{{ contextMenu.label }}</h3>
          <button
            class="w-full text-left p-1 px-2 text-sm italic hover:bg-gray-400"
            @click="startExplore([contextMenu.id])"
          >
            Start exploring with this publication
          </button>
        </div>
      </div>
    </template>
    <template v-slot:results>
      <ResultList :results="entities" class="text-left" />
    </template>
  </AppSkeletonSearch>
</template>

<script lang="ts">
import { defineComponent, computed, getCurrentInstance, ref, watch } from 'vue';
import { useStore } from 'vuex';
import AppSkeletonSearch from '@/components/Skeleton/AppSkeletonSearch.vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import SearchGraph from '@/components/Graphs/SearchGraph/SearchGraph.vue';
import ResultList from '@/components/Search/ResultList.vue';
import EventEmitter from 'events';
import {
  AnalysisResponse,
  AnalysisType,
  AuthorAnalysisType,
  GraphDrawingLayout,
  SearchResponse,
} from '@/models/Search';
import { ActiveLoader, PluginApi as Loading } from 'vue-loading-overlay';
import { getClusters } from '@/components/Graphs/utils/data';
import {
  GraphViewEmitter,
  analysisConfigurations,
} from '@/components/Graphs/utils/graphConfiguration';
import GraphControls from '@/components/Graphs/SearchGraph/GraphControls.vue';
import { SerializedNode } from 'graphology-types';

interface ContextMenu {
  x: number;
  y: number;
  id: number;
  label: string;
}

export default defineComponent({
  name: 'Search',
  components: {
    AppSkeletonSearch,
    GraphControls,
    SearchGraph,
    ResultList,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const searchContainer = ref<HTMLElement | null>(null);

    const app = getCurrentInstance();
    const loading = app?.appContext.config.globalProperties.$loading as Loading;
    const getLoader = () =>
      searchContainer.value
        ? loading.show({
            isFullPage: false,
            container: searchContainer.value,
          })
        : undefined;

    const query = computed(() => route.query.q);
    const entities = computed(() => store.getters['search/entities'] as SearchResponse[]);
    const analysis = computed(() => store.getters['search/analysis']() as AnalysisResponse);
    const isLoading = computed(() => store.getters['search/analysis']() as AnalysisResponse);
    const loader = ref<ActiveLoader | undefined>(undefined);
    watch(
      () => isLoading.value,
      (newValue) => {
        if (newValue) {
          if (!loader.value) loader.value = getLoader();
        } else {
          loader.value?.hide();
        }
      }
    );

    const selectedEntityIds = computed(() => store.getters['search/selectedEntityIds'] as number[]);
    const filterEntityIds = computed(() => store.getters['search/filterEntityIds'] as number[]);

    const currentAnalysis = computed(() => store.getters['search/currentAnalysis'] as AnalysisType);
    const clusters = computed(() =>
      analysis.value
        ? getClusters(analysis.value, entities.value.length)
        : {
            [-1]: { name: 'Unassigned', keywords: [], size: entities.value.length },
          }
    );

    const backdropActive = ref(true);
    const allEdges = ref(false);
    const activeCluster = ref<string | undefined>(undefined);
    const layout = ref(GraphDrawingLayout.FORCEATLAS2);

    const contextMenu = ref<ContextMenu | null>(null);

    const viewEventEmitter = new EventEmitter() as GraphViewEmitter;

    const manipulateView = (action: 'zoomIn' | 'reset' | 'zoomOut') => {
      switch (action) {
        case 'zoomIn':
          viewEventEmitter.emit('zoomIn');
          break;
        case 'reset':
          viewEventEmitter.emit('reset');
          break;
        case 'zoomOut':
          viewEventEmitter.emit('zoomOut');
          break;
      }
    };

    const switchAnalysis = async (newAnalysis: AnalysisType) => {
      await store.dispatch('search/switchAnalysis', { type: newAnalysis });
    };

    const clickNode = async ({
      node,
      event,
    }: {
      node: SerializedNode;
      event: MouseEvent & { original: MouseEvent };
    }) => {
      if (event.original.button === 2) {
        if (currentAnalysis.value === AuthorAnalysisType.COAUTHORNETWORK) return;

        event.original.stopPropagation();
        event.original.preventDefault();

        contextMenu.value = {
          x: event?.x - 5,
          y: event?.y + 5,
          id: parseInt(node.key.toString()),
          label: node.attributes?.label ?? '',
        };
      } else {
        store.dispatch('search/selectEntities', node ? { ids: [node.key] } : undefined);
      }
    };

    const filterNodes = async (nodes: number[]) => {
      store.dispatch('search/filterEntities', { ids: nodes });
    };

    watch(
      () => selectedEntityIds.value,
      ([newId]) => {
        if (newId) {
          const cluster = analysis.value?.entities.find((el) => parseInt(el.id) === newId)
            ?.properties.communityId;
          activeCluster.value = cluster ? cluster.toString() : undefined;
        }
      }
    );

    onBeforeRouteUpdate((to, from) => {
      if (to.query.q !== from.query.q) {
        store.dispatch('search/fetch', { query: to.query.q });
      }
    });

    const startExplore = async (entityIds: number[]) => {
      const loader = getLoader();
      await store.dispatch('explore/reset');
      await store.dispatch('explore/setEntities', { ids: entityIds });
      router.push({ name: 'Explore' });
      loader?.hide();
    };

    return {
      query,
      entities,
      selectedEntityIds,
      filterEntityIds,
      analysis,
      currentAnalysis,
      clusters,
      backdropActive,
      allEdges,
      activeCluster,
      layout,
      manipulateView,
      switchAnalysis,
      analysisConfigurations,
      viewEventEmitter,
      clickNode,
      filterNodes,
      startExplore,
      contextMenu,
      searchContainer,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.$store.dispatch('search/fetch', { query: to.query.q }));
  },
});
</script>
